import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, DollarSign, Sparkles } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { services } from '../mockData';

gsap.registerPlugin(ScrollTrigger);

export const Services = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const gridContainerRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current?.children || [], {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: { trigger: headerRef.current, start: 'top 80%' },
      });

      gsap.from(filterRef.current?.children || [], {
        y: 20,
        opacity: 0,
        duration: 0.5,
        stagger: 0.06,
        ease: 'power2.out',
        scrollTrigger: { trigger: filterRef.current, start: 'top 85%' },
      });

      gsap.from(bannerRef.current, {
        scale: 0.92,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: { trigger: bannerRef.current, start: 'top 85%' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Animate cards on filter change
  useEffect(() => {
    if (!gridContainerRef.current) return;
    const cards = gridContainerRef.current.querySelectorAll('.service-tile');
    gsap.fromTo(
      cards,
      { y: 40, opacity: 0, scale: 0.96 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.6,
        stagger: 0.05,
        ease: 'power3.out',
      }
    );
  }, [activeCategory]);

  const categories = ['all', ...services.map((s) => s.category)];
  const filteredServices =
    activeCategory === 'all'
      ? services
      : services.filter((s) => s.category === activeCategory);

  return (
    <section
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className="py-24 bg-gradient-to-b from-white to-pink-50/40 relative overflow-hidden"
    >
      {/* Decorative blob */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className="text-center mb-8 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full mb-5">
            <Sparkles className="w-4 h-4 text-pink-600" />
            <span className="text-sm font-medium text-pink-700">Our Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 bg-gradient-to-r from-pink-600 via-rose-500 to-fuchsia-600 bg-clip-text text-transparent leading-tight">
            Premium Nail Care
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            From classic manicures to luxury spa treatments — handcrafted, refined, and unforgettable.
          </p>
        </div>

        <div ref={filterRef} className="flex flex-wrap justify-center gap-3 mb-8 md:mb-14">
          {categories.map((category) => (
            <button
              key={category}
              data-testid={`service-filter-${category.toLowerCase().replace(/\s|&/g, '-')}`}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white shadow-lg shadow-pink-200 scale-105'
                  : 'bg-white text-gray-600 hover:bg-pink-50 border border-gray-200 hover:border-pink-200'
              }`}
            >
              {category === 'all' ? 'All Services' : category}
            </button>
          ))}
        </div>

        <div ref={gridContainerRef} className="space-y-8 md:space-y-14">
          {filteredServices.map((serviceCategory) => (
            <div key={serviceCategory.id}>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-7 flex items-center gap-3">
                <div className="w-1.5 h-9 bg-gradient-to-b from-pink-600 to-rose-500 rounded-full" />
                {serviceCategory.category}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {serviceCategory.items.map((service, index) => (
                  <Card
                    key={index}
                    data-testid={`service-card-${service.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="service-tile group relative overflow-hidden border-none bg-white/70 backdrop-blur-md shadow-md hover:shadow-2xl hover:shadow-pink-200/40 transition-all duration-500 cursor-pointer"
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const rotateX = ((y / rect.height) - 0.5) * -8;
                      const rotateY = ((x / rect.width) - 0.5) * 8;
                      gsap.to(e.currentTarget, {
                        rotateX,
                        rotateY,
                        transformPerspective: 1000,
                        duration: 0.4,
                        ease: 'power2.out',
                      });
                    }}
                    onMouseLeave={(e) => {
                      gsap.to(e.currentTarget, {
                        rotateX: 0,
                        rotateY: 0,
                        duration: 0.6,
                        ease: 'power3.out',
                      });
                    }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-rose-500 to-fuchsia-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    <CardHeader>
                      <CardTitle className="text-xl group-hover:text-pink-600 transition-colors">
                        {service.name}
                      </CardTitle>
                      <CardDescription className="text-gray-600">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-pink-600" />
                          <span className="text-2xl font-bold text-gray-800">{service.price}</span>
                        </div>
                        <Badge variant="secondary" className="flex items-center gap-1 bg-pink-50 text-pink-700 border border-pink-100">
                          <Clock className="w-3 h-3" />
                          {service.duration}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          ref={bannerRef}
          data-testid="walkin-banner"
          className="mt-20 relative overflow-hidden rounded-3xl p-10 md:p-14 text-white text-center shadow-2xl"
          style={{
            background:
              'linear-gradient(135deg, #ec4899 0%, #db2777 50%, #be185d 100%)',
          }}
        >
          <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.65'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
          <div className="relative">
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Walk-Ins Welcome</h3>
            <p className="text-base md:text-lg opacity-95 max-w-xl mx-auto">
              No appointment needed. We're here Monday through Friday, 9 AM – 6 PM. Just walk in and let us pamper you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
